/**
 * FlahaSOIL v2 — Base saturation composition chart.
 * Print-capture uses high-contrast ink for PDF readability.
 */
import { Box, Stack, Typography, Tooltip, Divider } from "@mui/material";
import type { ScientificChartData } from "../types/ScientificChartTypes";
import type { BaseSaturationChartData } from "../adapters/baseSaturationChartAdapter";
import { useChartTheme } from "../utils/useChartTheme";

interface BaseSaturationChartProps {
	chartData: ScientificChartData<BaseSaturationChartData | null>;
}

export function BaseSaturationChart({ chartData }: BaseSaturationChartProps) {
	const chartTheme = useChartTheme();
	const { data, accessibilitySummary, units } = chartData;
	const print = chartTheme.label === "#241A14";

	if (!data) {
		return (
			<Typography variant="body2" color="text.secondary">
				No chemistry data available for base saturation.
			</Typography>
		);
	}

	const cations = [
		{ label: "Ca", value: data.caPercent, color: chartTheme.cationCa, desc: "Calcium" },
		{ label: "Mg", value: data.mgPercent, color: chartTheme.cationMg, desc: "Magnesium" },
		{ label: "K", value: data.kPercent, color: chartTheme.cationK, desc: "Potassium" },
		{ label: "Na", value: data.naPercent, color: chartTheme.cationNa, desc: "Sodium (ESP)" },
		{ label: "Other", value: data.otherPercent, color: chartTheme.cationOther, desc: "Other cations" },
	];

	return (
		<Stack
			spacing={2}
			aria-label={accessibilitySummary}
			data-chart-core="baseSaturation"
			sx={{
				p: print ? 1.5 : 0,
				backgroundColor: print ? chartTheme.plotBg : "transparent",
				borderRadius: 1,
			}}
		>
			<Box
				sx={{
					height: print ? 40 : 32,
					width: "100%",
					display: "flex",
					borderRadius: 1,
					overflow: "hidden",
					border: `1px solid ${chartTheme.plotBorder}`,
					backgroundColor: chartTheme.barTrack,
					minHeight: print ? 40 : 36,
				}}
			>
				{cations.map((c) => (
					<Tooltip key={c.label} title={`${c.desc}: ${c.value.toFixed(1)}%`} arrow>
						<Box
							sx={{
								width: `${c.value}%`,
								height: "100%",
								backgroundColor: c.color,
								transition: "width 0.5s ease",
							}}
						/>
					</Tooltip>
				))}
			</Box>
			<Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
				{cations.map((c) => (
					<Stack key={c.label} direction="row" spacing={0.5} alignItems="center">
						<Box
							sx={{
								width: 12,
								height: 12,
								borderRadius: "50%",
								backgroundColor: c.color,
								border: `1px solid ${chartTheme.plotBorder}`,
							}}
						/>
						<Typography
							variant="body2"
							sx={{
								color: chartTheme.label,
								fontWeight: 600,
								fontSize: print ? "0.9rem" : undefined,
							}}
						>
							{c.label} {c.value.toFixed(1)}%
						</Typography>
					</Stack>
				))}
			</Stack>
			<Divider sx={{ borderColor: chartTheme.plotBorder }} />
			<Stack direction="row" justifyContent="space-between">
				<Typography
					variant="body2"
					sx={{ color: chartTheme.label, fontWeight: 600, fontSize: print ? "0.95rem" : undefined }}
				>
					Base Saturation
				</Typography>
				<Typography
					variant="body2"
					fontWeight={700}
					sx={{ color: chartTheme.label, fontSize: print ? "0.95rem" : undefined }}
				>
					{data.baseSaturation.toFixed(1)}%
				</Typography>
			</Stack>
			<Stack direction="row" justifyContent="space-between">
				<Typography
					variant="body2"
					sx={{ color: chartTheme.label, fontWeight: 600, fontSize: print ? "0.95rem" : undefined }}
				>
					CEC
				</Typography>
				<Typography
					variant="body2"
					fontWeight={700}
					sx={{ color: chartTheme.label, fontSize: print ? "0.95rem" : undefined }}
				>
					{data.cec.toFixed(1)} {units.cec}
				</Typography>
			</Stack>
		</Stack>
	);
}
